'use client'

import { Loader2 } from 'lucide-react'

import { cn } from '../cn'
import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'
import { Progress } from './progress'

/**
 * StatusCard — long-running task with progress + optional cancel.
 * Output card; updates async via `message:card_update` events the host
 * applies to the existing card. Per
 * `apps/agent/plans/plan-action-cards.md` Phase 4.
 *
 * State machine: queued → running → done | error | cancelled.
 * Cancellable cards expose a Cancel button that the host wires to
 * whatever cancel pathway the source tool supports.
 */

export type StatusState = 'queued' | 'running' | 'done' | 'error' | 'cancelled'

export interface StatusStage {
  label: string
  state: 'queued' | 'running' | 'done' | 'error'
}

export interface StatusCardProps {
  title: string
  description?: string
  attribution?: string
  state: StatusState
  /** Numeric progress; if `total` is set the bar shows value/total. */
  progress?: { value: number; total?: number; label?: string }
  /** Multi-stage breakdown shown as a vertical step list. */
  stages?: StatusStage[]
  cancellable?: boolean
  onCancel?: () => void
}

const STAGE_DOT: Record<StatusStage['state'], string> = {
  queued: 'bg-muted-foreground/30',
  running: 'bg-amber-500 animate-pulse',
  done: 'bg-emerald-500',
  error: 'bg-destructive',
}

export function StatusCard({
  title,
  description,
  attribution,
  state,
  progress,
  stages,
  cancellable,
  onCancel,
}: StatusCardProps) {
  const tone: ActionCardTone =
    state === 'done' ? 'ok' : state === 'error' ? 'destructive' : state === 'cancelled' ? 'neutral' : 'pending'
  const showCancel = cancellable && (state === 'queued' || state === 'running')

  const pct = progress?.total ? Math.round((progress.value / progress.total) * 100) : undefined

  return (
    <ActionCard
      title={title}
      description={description}
      attribution={attribution}
      tone={tone}
      footer={
        showCancel ? (
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        ) : null
      }>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {state === 'running' || state === 'queued' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
          <span className="font-medium capitalize">{state}</span>
          {progress?.label ? <span>· {progress.label}</span> : null}
          {pct !== undefined ? <span>· {pct}%</span> : null}
        </div>
        {progress?.total && pct !== undefined ? <Progress value={pct} className="h-1.5" /> : null}
        {stages && stages.length > 0 ? (
          <ol className="mt-1 flex flex-col gap-1.5 text-xs">
            {stages.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', STAGE_DOT[s.state])} aria-hidden />
                <span className={cn('text-foreground', s.state === 'queued' && 'text-muted-foreground')}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </ActionCard>
  )
}
